/*
 * Go Kids — send the contact form through the Rosscor Brands mail service so the
 * client gets a branded notification instead of Shopify's plain text one.
 *
 * Progressive enhancement over {% form 'contact' %}: with the theme settings blank,
 * or if this script never runs, the form posts to Shopify exactly as before. The
 * endpoint requires a Cloudflare Turnstile token, so the widget is rendered into the
 * form and the submit button waits for it.
 *
 * Delegated on document in the capture phase for the same reason as gk-klaviyo.js:
 * Shopify's storefront captcha script binds its own submit listener and re-submits
 * natively via form.submit(), which ignores preventDefault().
 */
(function () {
  'use strict';

  var TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  var CHECKING = 'Checking your browser…';
  var REQUEST_TIMEOUT = 25000;

  function fallbackEmail(form) {
    return (form.getAttribute('data-gk-contact-email') || '').trim();
  }

  function unavailableText(form) {
    var email = fallbackEmail(form);
    return 'Security check unavailable. Please reload the page to retry' + (email ? ', or email ' + email + '.' : '.');
  }

  function genericErrorText(form) {
    var email = fallbackEmail(form);
    return 'Something went wrong sending your message.' + (email ? ' Please email ' + email + ' instead.' : ' Please try again.');
  }

  var states = new WeakMap();
  var loading;

  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (loading) return loading;
    loading = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      var timer = window.setTimeout(fail, 15000);
      function fail() {
        window.clearTimeout(timer);
        script.remove();
        loading = undefined;
        reject(new Error('Security check unavailable'));
      }
      script.src = TURNSTILE_SRC;
      script.async = true;
      script.onerror = fail;
      script.onload = function () {
        window.clearTimeout(timer);
        if (window.turnstile) resolve(window.turnstile);
        else fail();
      };
      document.head.appendChild(script);
    });
    return loading;
  }

  function submitButton(form) {
    return form.querySelector('button[type="submit"], button:not([type])');
  }

  function setStatus(form, text) {
    var el = form.querySelector('[data-gk-contact-status]');
    if (el) el.textContent = text;
  }

  function setReady(form, ready) {
    var state = states.get(form);
    state.ready = ready;
    var button = submitButton(form);
    if (button) button.disabled = !ready || state.busy;
  }

  function resetVerification(form) {
    var state = states.get(form);
    state.token = '';
    setStatus(form, CHECKING);
    setReady(form, false);
    if (state.widget !== undefined && window.turnstile) window.turnstile.reset(state.widget);
  }

  function mount(form) {
    var container = form.querySelector('[data-gk-contact-verification]');
    if (!container || states.has(form)) return;
    var state = { ready: false, busy: false, token: '', widget: undefined };
    states.set(form, state);
    setStatus(form, CHECKING);
    setReady(form, false);

    loadTurnstile()
      .then(function (api) {
        state.widget = api.render(container, {
          sitekey: container.getAttribute('data-sitekey'),
          // The server verifies this exact action name.
          action: 'contact',
          theme: 'light',
          size: container.clientWidth < 300 ? 'compact' : 'flexible',
          callback: function (token) {
            state.token = token;
            setStatus(form, '');
            setReady(form, true);
          },
          'expired-callback': function () {
            resetVerification(form);
          },
          'timeout-callback': function () {
            resetVerification(form);
          },
          'error-callback': function () {
            setStatus(form, unavailableText(form));
            setReady(form, false);
            return true;
          },
        });
      })
      .catch(function () {
        setStatus(form, unavailableText(form));
        setReady(form, false);
      });
  }

  function field(form, name) {
    var el = form.elements['contact[' + name + ']'];
    return el ? String(el.value || '') : '';
  }

  function show(el) {
    if (el) el.hidden = false;
  }

  function hide(el) {
    if (el) el.hidden = true;
  }

  function showError(form, text) {
    var errorEl = form.querySelector('[data-gk-contact-error]');
    if (!errorEl) return;
    show(errorEl);
    var target = errorEl.querySelector('[data-gk-contact-error-text]') || errorEl;
    target.textContent = text;
    errorEl.focus();
  }

  function handle(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[data-gk-contact]')) return;
    var endpoint = form.getAttribute('data-gk-contact');
    if (!endpoint) return; // settings blank: Shopify's own contact email

    // Claim the submit before anything else can see it (see header comment).
    event.preventDefault();
    event.stopImmediatePropagation();

    if (form.checkValidity && !form.checkValidity()) {
      if (form.reportValidity) form.reportValidity();
      return;
    }

    var state = states.get(form);
    if (!state) {
      mount(form);
      state = states.get(form);
    }
    if (!state || state.busy) return;

    var genericError = genericErrorText(form);
    var errorEl = form.querySelector('[data-gk-contact-error]');
    hide(errorEl);

    if (!state.ready || !state.token) {
      setStatus(form, CHECKING);
      return;
    }

    var honeypot = form.querySelector('[data-gk-contact-hp]');
    var payload = {
      brand: form.getAttribute('data-gk-contact-brand') || 'go-kids',
      name: field(form, 'name').trim(),
      email: field(form, 'email').trim(),
      phone: field(form, 'phone').trim(),
      topic: field(form, 'topic').trim(),
      message: field(form, 'body').trim(),
      website: honeypot ? honeypot.value : '',
      'cf-turnstile-response': state.token,
    };

    var controller = new AbortController();
    var timer = window.setTimeout(function () {
      controller.abort();
    }, REQUEST_TIMEOUT);
    state.busy = true;
    setReady(form, state.ready);
    form.setAttribute('aria-busy', 'true');

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller.signal,
    })
      .then(function (response) {
        return response
          .json()
          .catch(function () {
            return null;
          })
          .then(function (body) {
            if (response.ok && body && body.ok === true) return;
            // The service's messages are written for visitors; anything else is ours.
            var detail = body && typeof body.error === 'string' && body.error;
            throw new Error(detail || genericError);
          });
      })
      .then(function () {
        hide(form.querySelector('[data-gk-contact-fields]'));
        var successEl = form.querySelector('[data-gk-contact-success]');
        show(successEl);
        if (successEl) successEl.focus();
        // A finished form must not start another security challenge.
        if (state.widget !== undefined && window.turnstile) window.turnstile.remove(state.widget);
        state.widget = undefined;
      })
      .catch(function (error) {
        // Only the Error we threw above carries visitor-facing text; a rejected fetch
        // (TypeError) or the timeout (AbortError) must not leak browser wording.
        var known = error && error.name === 'Error' && error.message;
        showError(form, known || genericError);
        // Tokens are single-use: get a fresh one before the visitor retries.
        resetVerification(form);
      })
      .then(function () {
        window.clearTimeout(timer);
        state.busy = false;
        form.removeAttribute('aria-busy');
        setReady(form, state.ready);
      });
  }

  function init() {
    var forms = document.querySelectorAll('form[data-gk-contact]');
    Array.prototype.forEach.call(forms, function (form) {
      if (!form.getAttribute('data-gk-contact')) return;
      // Load the challenge only once the form is near the viewport.
      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(
          function (entries) {
            if (entries.some(function (entry) { return entry.isIntersecting; })) {
              observer.disconnect();
              mount(form);
            }
          },
          { rootMargin: '200px' }
        );
        observer.observe(form);
      } else {
        mount(form);
      }
    });
  }

  document.addEventListener('submit', handle, true);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
